import { Card } from '@yleisradio/areena-types';
import { useCallback, useEffect, useRef } from 'react';
import { isSameCard } from 'utils/card';

// Context menu trigger, or the card link if the card has no menu
const FOCUS_TARGETS = '[aria-haspopup="menu"], a[href]';

type FocusPosition = { list: Element; position: number };

/**
 * Keeps keyboard focus in the list when a card is replaced or removed.
 * Focus moves to the card taking its place, or to the previous card
 * if the last one was removed.
 */
export function useCardUpdateFocus(cards: Card[]) {
  const itemRefs = useRef<(HTMLElement | null)[]>([]);
  const pendingFocus = useRef<FocusPosition | null>(null);
  // Latest cards, as rememberFocus is called after an async request
  const currentCards = useRef(cards);

  const setItemRef = useCallback(
    (index: number) => (element: HTMLElement | null) => {
      itemRefs.current[index] = element;
    },
    []
  );

  // Call before updating the target card
  const rememberFocus = useCallback((target: Card) => {
    const index = currentCards.current.findIndex((c) => isSameCard(c, target));
    const item = itemRefs.current[index];
    const list = item?.parentElement;
    if (!item || !list || !item.contains(document.activeElement)) return;

    pendingFocus.current = {
      list,
      position: Array.prototype.indexOf.call(list.children, item),
    };
  }, []);

  useEffect(() => {
    currentCards.current = cards;

    const pending = pendingFocus.current;
    if (!pending) return;
    pendingFocus.current = null;

    const { children } = pending.list;
    const item = children[pending.position] ?? children[pending.position - 1];
    item?.querySelector<HTMLElement>(FOCUS_TARGETS)?.focus();
  }, [cards]);

  return { setItemRef, rememberFocus };
}
